import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import api from '../services/api';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Start with Firebase user data (guaranteed to work)
        const baseUser = {
          firebaseUid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName
        };

        try {
          // Try to get additional profile from backend
          const token = await firebaseUser.getIdToken();
          const response = await api.get('/auth/me', {
            headers: { Authorization: `Bearer ${token}` }
          });

          // Merge backend profile with Firebase user data
          setUser({
            ...baseUser,
            ...response.data
          });
        } catch (error) {
          // Backend profile fetch failed, but user is still authenticated via Firebase
          // Log the error but don't block the user
          console.warn('Could not fetch user profile from backend:', error?.message || error);

          // Still set the user with Firebase data
          setUser(baseUser);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    setUser,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}